import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Thermometer, Snowflake, Refrigerator, Zap, Droplets, Wrench, ChevronRight, User, LogOut, Home, HelpCircle, Phone, Shield, MapPin, Building, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import AuthDialog from './AuthDialog';

const Navbar = ({ transparent = false }) => {
  const [showAuth, setShowAuth] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const token = localStorage.getItem('auth_token');
  const location = useLocation();
  const navigate = useNavigate();
  
  const isHomePage = location.pathname === '/';
  const useTransparent = transparent || isHomePage;

  // Hizmet kategorileri
  const serviceCategories = [
    { id: 'kombi', name: 'Kombi Servisi', icon: Thermometer, color: 'bg-orange-500' },
    { id: 'klima', name: 'Klima Servisi', icon: Snowflake, color: 'bg-blue-500' },
    { id: 'beyaz_esya', name: 'Beyaz Eşya', icon: Refrigerator, color: 'bg-teal-500' },
    { id: 'elektrik', name: 'Elektrik', icon: Zap, color: 'bg-yellow-500' },
    { id: 'su_tesisati', name: 'Su Tesisatı', icon: Droplets, color: 'bg-cyan-500' },
    { id: 'genel', name: 'Diğer Hizmetler', icon: Wrench, color: 'bg-gray-500' },
  ];

  // Popüler Şehirler
  const populerSehirler = [
    { name: 'Ankara', slug: 'ankara-kombi-servisi', icon: '🏛️' },
    { name: 'İstanbul', slug: 'istanbul-kombi-servisi', icon: '🌉' },
    { name: 'İzmir', slug: 'izmir-kombi-servisi', icon: '⚓' },
  ];

  const [showCitiesDropdown, setShowCitiesDropdown] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Menü açıkken scroll'u engelle
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileMenuOpen]);

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    setMobileMenuOpen(false);
    window.location.href = '/';
  };

  const handleServiceClick = (serviceId) => {
    setMobileMenuOpen(false);
    // Direkt form sayfasına git - giriş form sonunda istenecek
    navigate(`/servis-talep/${serviceId}`);
  };

  const navbarBg = useTransparent && !scrolled 
    ? 'bg-transparent' 
    : 'bg-[#0A0E1A] border-b border-white/10';
  
  const textColor = useTransparent && !scrolled 
    ? 'text-white' 
    : 'text-white';

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navbarBg}`}>
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-16 py-1 md:py-2">
          {/* Mobil Layout */}
          <div className="lg:hidden flex items-center justify-between py-1">
            <button 
              className={`${textColor} p-2 -ml-2`}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-button"
            >
              <Menu size={26} />
            </button>
            
            {/* Logo - Mobilde menü kapalıyken ortada göster */}
            {!mobileMenuOpen && (
              <Link to="/" className="flex items-center absolute left-1/2 transform -translate-x-1/2">
                <img 
                  src="/logo-custom.png"
                  alt="HizmetGeliyo" 
                  className="h-20 object-contain"
                  width="200"
                  height="80"
                />
              </Link>
            )}
            
            {/* Sağ taraf - Giriş butonu */}
            {token ? (
              <Link to="/hesabim" className={`${textColor} p-2`}>
                <User size={24} />
              </Link>
            ) : (
              <button 
                onClick={() => setShowAuth(true)}
                className={`${textColor} p-2`}
              >
                <User size={24} />
              </button>
            )}
          </div>

          {/* Desktop Layout */}
          <div className="hidden lg:flex items-center justify-between">
            <div className="flex items-center gap-8">
              <Link to="/" className="flex items-center">
                <img 
                  src="/logo-custom.png"
                  alt="HizmetGeliyo" 
                  className="h-24 object-contain transition-all duration-300"
                  width="240"
                  height="96"
                />
              </Link>
              
              <nav className="flex items-center gap-6">
                <Link to="/hizmetler" className={`${textColor} hover:opacity-80 font-medium transition-colors`}>Hizmetler</Link>
                <Link to="/hakkimizda" className={`${textColor} hover:opacity-80 font-medium transition-colors`}>Kurumsal</Link>
                <Link to="/iletisim" className={`${textColor} hover:opacity-80 font-medium transition-colors`}>İletişim</Link>
              </nav>
            </div>
            
            <div className="flex items-center gap-4">
              {token ? (
                <>
                  <Link to="/hesabim" className={`${textColor} hover:opacity-80 font-medium transition-colors`}>Hesabım</Link>
                  <Button 
                    onClick={handleLogout} 
                    className="bg-[#E29578] hover:bg-[#D67A56] text-white rounded-full px-6"
                    data-testid="logout-button"
                  >
                    Çıkış
                  </Button>
                </>
              ) : (
                <Button 
                  onClick={() => setShowAuth(true)} 
                  className={`${scrolled ? 'bg-[#006D77] hover:bg-[#005a63] text-white' : 'bg-white/20 hover:bg-white/30 backdrop-blur-sm border border-white/30 text-white'} rounded-full px-6`}
                  data-testid="login-button"
                >
                  Giriş Yap / Üye Ol
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Full Screen Mobil Menü */}
      <div 
        className={`lg:hidden fixed inset-0 z-40 transform transition-transform duration-300 ease-in-out ${
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Backdrop */}
        <div 
          className={`absolute inset-0 bg-black/50 transition-opacity duration-300 ${
            mobileMenuOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={() => setMobileMenuOpen(false)}
        />
        
        {/* Menü İçeriği */}
        <div className="absolute left-0 top-0 bottom-0 w-[85%] max-w-sm bg-white shadow-2xl overflow-y-auto">
          {/* Menü Header - Logo ve Kapat */}
          <div className="sticky top-0 bg-gradient-to-r from-[#006D77] to-[#0A9396] px-4 py-5">
            <div className="flex items-center justify-between">
              <Link to="/" onClick={() => setMobileMenuOpen(false)} className="flex-1 flex justify-center">
                <img 
                  src="/logo-custom.png"
                  alt="HizmetGeliyo" 
                  className="h-16 object-contain"
                  width="160"
                  height="64"
                />
              </Link>
              <button 
                onClick={() => setMobileMenuOpen(false)}
                className="text-white/80 hover:text-white p-2 bg-white/10 rounded-full absolute right-4"
              >
                <X size={20} />
              </button>
            </div>
            
            {/* Kullanıcı Durumu */}
            {token ? (
              <div className="flex items-center gap-3 mt-4">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <User className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-white font-medium">Hoş Geldiniz</p>
                  <Link 
                    to="/hesabim" 
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-white/80 text-sm flex items-center gap-1 hover:text-white"
                  >
                    Hesabıma Git <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            ) : (
              <Button 
                onClick={() => { setShowAuth(true); setMobileMenuOpen(false); }}
                className="w-full bg-white text-[#006D77] hover:bg-gray-100 rounded-xl font-medium mt-4"
              >
                Giriş Yap / Üye Ol
              </Button>
            )}
          </div>

          {/* Hizmet Kategorileri */}
          <div className="p-4">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
              Hizmetler
            </h3>
            <div className="grid grid-cols-3 gap-2">
              {serviceCategories.map((service) => {
                const Icon = service.icon;
                return (
                  <button
                    key={service.id}
                    onClick={() => handleServiceClick(service.id)}
                    className="flex flex-col items-center p-3 rounded-xl bg-gray-50 hover:bg-gray-100 active:bg-gray-200 transition-colors"
                    data-testid={`menu-service-${service.id}`}
                  >
                    <div className={`w-10 h-10 ${service.color} rounded-full flex items-center justify-center mb-2`}>
                      <Icon className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-xs text-gray-700 text-center leading-tight">{service.name}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Menü Linkleri */}
          <div className="px-4 pb-4">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
              Menü
            </h3>
            <nav className="space-y-1">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-gray-50 transition-colors"
              >
                <Home className="w-5 h-5 text-gray-400" />
                <span className="text-gray-700 font-medium">Ana Sayfa</span>
              </Link>
              <Link
                to="/hizmetler"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-gray-50 transition-colors"
              >
                <Wrench className="w-5 h-5 text-gray-400" />
                <span className="text-gray-700 font-medium">Tüm Hizmetler</span>
              </Link>
              <Link
                to="/nasil-calisir"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-gray-50 transition-colors"
              >
                <HelpCircle className="w-5 h-5 text-gray-400" />
                <span className="text-gray-700 font-medium">Nasıl Çalışır</span>
              </Link>
              <Link
                to="/iletisim"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-gray-50 transition-colors"
              >
                <Phone className="w-5 h-5 text-gray-400" />
                <span className="text-gray-700 font-medium">İletişim</span>
              </Link>
            </nav>
          </div>

          {/* Usta Girişi Banner */}
          <div className="mx-4 mb-4 p-4 bg-gradient-to-r from-[#E29578] to-[#D67A56] rounded-xl">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <p className="text-white font-medium text-sm">Usta mısınız?</p>
                <p className="text-white/80 text-xs">Hemen kaydolun, iş fırsatlarını kaçırmayın</p>
              </div>
            </div>
            <Link
              to="/prousta"
              onClick={() => setMobileMenuOpen(false)}
              className="mt-3 block w-full bg-white text-[#E29578] text-center py-2 rounded-lg font-medium text-sm hover:bg-gray-100 transition-colors"
            >
              Usta Girişi
            </Link>
          </div>

          {/* Çıkış Butonu (Giriş yapıldıysa) */}
          {token && (
            <div className="px-4 pb-6">
              <button
                onClick={handleLogout}
                className="flex items-center gap-3 px-3 py-3 w-full rounded-xl hover:bg-red-50 transition-colors text-red-500"
              >
                <LogOut className="w-5 h-5" />
                <span className="font-medium">Çıkış Yap</span>
              </button>
            </div>
          )}
        </div>
      </div>

      <AuthDialog open={showAuth} onClose={() => setShowAuth(false)} />
    </>
  );
};

export default Navbar;
